import { getHistoryActions } from "../stores/historyStore";
import { getAssetActions, getAssetById } from "../stores/assetStore";
import { getSceneActions, getSceneState } from "../stores/sceneStore";
import { useSelectionStore } from "../stores/selectionStore";
import { logger } from "./logger";
import { namesFromRecord, nextUniqueName } from "./uniqueNames.js";

const USER_OBJECT_BUNDLE_VERSION = 1;

function cloneStructured(value) {
  if (value == null) return value;
  try {
    return structuredClone(value);
  } catch {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }
}

function worldBodyUuid(worldBody) {
  return typeof worldBody === "string" ? worldBody : worldBody?.uuid;
}

function collectBodySubtree(bodies, rootBodyUuid) {
  const result = [];
  const visit = (bodyUuid) => {
    const body = bodies?.[bodyUuid];
    if (!body) return;
    result.push(bodyUuid);
    (body.children || []).forEach(visit);
  };
  visit(rootBodyUuid);
  return result;
}

function geomUuidsForBodies(bodyEntries) {
  const uuids = new Set();
  Object.values(bodyEntries).forEach((body) => {
    (body?.geoms || []).forEach((geom) => {
      if (geom?.uuid) uuids.add(String(geom.uuid));
    });
  });
  return uuids;
}

function materialRefsForBodies(bodyEntries, sites) {
  const refs = new Set();
  Object.values(bodyEntries).forEach((body) => {
    (body?.geoms || []).forEach((geom) => {
      (geom?.materials || []).forEach((materialId) => {
        if (materialId) refs.add(String(materialId));
      });
    });
    (body?.sites || []).forEach((siteUuid) => {
      const materialId = sites?.[siteUuid]?.material;
      if (materialId) refs.add(String(materialId));
    });
  });
  return refs;
}

function meshRefsForBodies(bodyEntries) {
  const refs = new Set();
  Object.values(bodyEntries).forEach((body) => {
    (body?.geoms || []).forEach((geom) => {
      if (geom?.mesh) refs.add(String(geom.mesh));
    });
  });
  return refs;
}

function textureRefsForMaterials(materials) {
  const refs = new Set();
  Object.values(materials || {}).forEach((material) => {
    if (material?.colorMap) refs.add(String(material.colorMap));
    if (material?.alphaMap) refs.add(String(material.alphaMap));
  });
  return refs;
}

function tendonTouchesBundle(tendon, jointUuids, siteUuids, geomUuids) {
  if (!tendon) return false;
  if (
    Array.isArray(tendon.joints) &&
    tendon.joints.some((entry) => entry?.joint && jointUuids.has(String(entry.joint)))
  ) {
    return true;
  }
  if (
    Array.isArray(tendon.spatial) &&
    tendon.spatial.some(
      (entry) =>
        (entry?.site && siteUuids.has(String(entry.site))) ||
        (entry?.geom && geomUuids.has(String(entry.geom))) ||
        (entry?.sidesite && siteUuids.has(String(entry.sidesite)))
    )
  ) {
    return true;
  }
  return false;
}

function assetRegistrationFromEntry(entry) {
  if (!entry?.id || !entry?.kind) return null;
  return {
    id: entry.id,
    kind: entry.kind,
    name: entry.name,
    status: entry.status,
    bytes: entry.bytes,
    hash: entry.hash,
    thumbnailDataUrl: entry.thumbnailDataUrl,
    sources: cloneStructured(entry.sources),
    ...(entry.kind === "mesh" && entry.vertexCount !== undefined
      ? { vertexCount: entry.vertexCount }
      : {}),
    ...(entry.kind === "texture"
      ? {
          ...(entry.width !== undefined ? { width: entry.width } : {}),
          ...(entry.height !== undefined ? { height: entry.height } : {}),
        }
      : {}),
  };
}

export function createUserLibraryObjectBundle(rootBodyUuid, sceneState = getSceneState()) {
  const sourceBody = sceneState?.bodies?.[rootBodyUuid];
  if (!sourceBody) return null;
  const bodyUuids = collectBodySubtree(sceneState.bodies, rootBodyUuid);
  if (bodyUuids.length === 0) return null;

  const bodyUuidSet = new Set(bodyUuids);
  const bodies = {};
  const joints = {};
  const sites = {};
  for (const bodyUuid of bodyUuids) {
    const body = cloneStructured(sceneState.bodies[bodyUuid]);
    if (!body) continue;
    bodies[bodyUuid] = body;
    (body.joints || []).forEach((jointUuid) => {
      const joint = sceneState.joints?.[jointUuid];
      if (joint) joints[jointUuid] = cloneStructured(joint);
    });
    (body.sites || []).forEach((siteUuid) => {
      const site = sceneState.sites?.[siteUuid];
      if (site) sites[siteUuid] = cloneStructured(site);
    });
  }

  const jointUuids = new Set(Object.keys(joints));
  const siteUuids = new Set(Object.keys(sites));
  const geomUuids = geomUuidsForBodies(bodies);
  const materialRefs = materialRefsForBodies(bodies, sites);
  const materials = {};
  materialRefs.forEach((materialId) => {
    const material = sceneState.materials?.[materialId];
    if (material) materials[materialId] = cloneStructured(material);
  });
  const textureRefs = textureRefsForMaterials(materials);
  const textures = {};
  textureRefs.forEach((textureId) => {
    const texture = sceneState.textures?.[textureId];
    if (texture) textures[textureId] = cloneStructured(texture);
  });

  const meshRefs = meshRefsForBodies(bodies);
  const meshes = {};
  const meshAssets = {};
  meshRefs.forEach((meshId) => {
    const mesh = sceneState.meshes?.[meshId];
    if (!mesh) return;
    meshes[meshId] = cloneStructured(mesh);
    const assetId = typeof mesh.fingerprint === "string" ? mesh.fingerprint.trim() : "";
    if (!assetId || meshAssets[assetId]) return;
    const asset = getAssetById(assetId);
    const registration = assetRegistrationFromEntry(asset);
    if (registration) meshAssets[assetId] = registration;
  });

  const tendons = {};
  Object.entries(sceneState.tendons || {}).forEach(([tendonUuid, tendon]) => {
    if (tendonTouchesBundle(tendon, jointUuids, siteUuids, geomUuids)) {
      tendons[tendonUuid] = cloneStructured(tendon);
    }
  });

  const actuators = {};
  const tendonUuids = new Set(Object.keys(tendons));
  Object.entries(sceneState.actuators || {}).forEach(([actuatorUuid, actuator]) => {
    if (
      (actuator?.joint && jointUuids.has(String(actuator.joint))) ||
      (actuator?.tendonUuid && tendonUuids.has(String(actuator.tendonUuid)))
    ) {
      actuators[actuatorUuid] = cloneStructured(actuator);
    }
  });

  const sourceWorldBody = (sceneState.worldBodies || []).find(
    (worldBody) => worldBodyUuid(worldBody) === rootBodyUuid
  );

  return {
    version: USER_OBJECT_BUNDLE_VERSION,
    kind: "body-subtree",
    rootBodyUuid,
    worldBodyName: typeof sourceWorldBody === "string" ? sourceBody.name : sourceWorldBody?.name,
    bodies,
    joints,
    sites,
    meshes,
    meshAssets,
    materials,
    textures,
    tendons,
    actuators,
    bodyUuidSet: Array.from(bodyUuidSet),
  };
}

function remapTendon(tendon, jointUuidMap, siteUuidMap, geomUuidMap) {
  const cloned = cloneStructured(tendon);
  if (!cloned || typeof cloned !== "object") return null;
  delete cloned.uuid;

  if (Array.isArray(cloned.joints)) {
    const nextJoints = cloned.joints.map((entry) => {
      const nextJoint = entry?.joint ? jointUuidMap.get(String(entry.joint)) : null;
      return nextJoint ? { ...entry, joint: nextJoint } : null;
    });
    if (nextJoints.some((entry) => !entry)) return null;
    cloned.joints = nextJoints;
  }

  if (Array.isArray(cloned.spatial)) {
    const nextSpatial = cloned.spatial.map((entry) => {
      if (!entry || typeof entry !== "object") return entry;
      if (entry.kind === "site") {
        const nextSite = entry.site ? siteUuidMap.get(String(entry.site)) : null;
        return nextSite ? { ...entry, site: nextSite } : null;
      }
      if (entry.kind === "geom") {
        const nextGeom = entry.geom ? geomUuidMap.get(String(entry.geom)) : null;
        const nextSideSite = entry.sidesite ? siteUuidMap.get(String(entry.sidesite)) : undefined;
        if (!nextGeom || (entry.sidesite && !nextSideSite)) return null;
        return { ...entry, geom: nextGeom, ...(nextSideSite ? { sidesite: nextSideSite } : {}) };
      }
      return entry;
    });
    if (nextSpatial.some((entry) => !entry)) return null;
    cloned.spatial = nextSpatial;
  }

  return cloned;
}

export function insertUserLibraryObjectBundle(bundle, targetParentUuid = null) {
  if (!bundle || bundle.version !== USER_OBJECT_BUNDLE_VERSION || bundle.kind !== "body-subtree") {
    return null;
  }
  const sourceBodies = bundle.bodies || {};
  if (!sourceBodies[bundle.rootBodyUuid]) return null;

  const sceneActions = getSceneActions();
  const bodyUuidMap = new Map();
  const jointUuidMap = new Map();
  const siteUuidMap = new Map();
  const geomUuidMap = new Map();
  const tendonUuidMap = new Map();
  const meshKeyMap = new Map();
  const textureKeyMap = new Map();
  const materialKeyMap = new Map();

  const meshAssetRegistrations = Object.values(bundle.meshAssets || {}).filter(Boolean);
  if (meshAssetRegistrations.length > 0) {
    getAssetActions().registerAssets(meshAssetRegistrations);
  }

  Object.entries(bundle.meshes || {}).forEach(([sourceMeshId, mesh]) => {
    const clonedMesh = cloneStructured(mesh);
    if (!clonedMesh || typeof clonedMesh !== "object") return;
    delete clonedMesh.uuid;
    const newMeshUuid = sceneActions.addMesh(clonedMesh);
    if (newMeshUuid) meshKeyMap.set(String(sourceMeshId), String(newMeshUuid));
  });

  if (
    Object.keys(bundle.textures || {}).length > 0 &&
    typeof sceneActions.setTextures === "function"
  ) {
    sceneActions.setTextures(bundle.textures);
    const loadedTextures = getSceneState().textures || {};
    Object.entries(bundle.textures || {}).forEach(([sourceTextureId, texture]) => {
      const byId = loadedTextures[sourceTextureId];
      const byName = Object.values(loadedTextures).find(
        (entry) => entry?.name && entry.name === texture?.name
      );
      const targetTextureId = byId?.uuid || byName?.uuid || sourceTextureId;
      textureKeyMap.set(String(sourceTextureId), String(targetTextureId));
    });
  }

  if (
    Object.keys(bundle.materials || {}).length > 0 &&
    typeof sceneActions.loadMaterials === "function"
  ) {
    const materials = {};
    Object.entries(bundle.materials || {}).forEach(([materialId, material]) => {
      const cloned = cloneStructured(material);
      if (cloned?.colorMap && textureKeyMap.has(String(cloned.colorMap))) {
        cloned.colorMap = textureKeyMap.get(String(cloned.colorMap));
      }
      if (cloned?.alphaMap && textureKeyMap.has(String(cloned.alphaMap))) {
        cloned.alphaMap = textureKeyMap.get(String(cloned.alphaMap));
      }
      materials[materialId] = cloned;
    });
    const keyMap = sceneActions.loadMaterials(materials) || {};
    Object.entries(bundle.materials || {}).forEach(([sourceMaterialId, material]) => {
      const targetMaterialId =
        keyMap[sourceMaterialId] ||
        (material?.name ? keyMap[material.name] : null) ||
        sourceMaterialId;
      materialKeyMap.set(String(sourceMaterialId), String(targetMaterialId));
      if (material?.name) materialKeyMap.set(String(material.name), String(targetMaterialId));
    });
  }

  const insertBody = (sourceBodyUuid, parentUuid) => {
    const sceneState = getSceneState();
    const sourceBody = sourceBodies[sourceBodyUuid];
    if (!sourceBody) return null;
    const nextBodyName = nextUniqueName(sourceBody.name, namesFromRecord(sceneState?.bodies), "_");
    const nextWorldBodyName =
      !parentUuid && bundle.worldBodyName
        ? nextUniqueName(
            bundle.worldBodyName,
            (sceneState?.worldBodies || [])
              .map((worldBody) => (typeof worldBody === "string" ? null : worldBody?.name))
              .filter(Boolean),
            " "
          )
        : null;
    const clonedInertia = sourceBody.inertia ? cloneStructured(sourceBody.inertia) : undefined;
    if (clonedInertia && typeof clonedInertia === "object") delete clonedInertia.uuid;
    const clonedFrames = Array.isArray(sourceBody.frames)
      ? sourceBody.frames.map((frame) => {
          const cloned = cloneStructured(frame);
          if (cloned && typeof cloned === "object") delete cloned.uuid;
          return cloned;
        })
      : undefined;
    const newBodyUuid = sceneActions.addBody(
      {
        name: nextBodyName,
        position: cloneStructured(sourceBody.position) ?? [0, 0, 0],
        orientation: cloneStructured(sourceBody.orientation) ?? [1, 0, 0, 0],
        isMocap: sourceBody.isMocap,
        gravityCompensation: sourceBody.gravityCompensation,
        inertia: clonedInertia,
        frames: clonedFrames,
        geoms: [],
        joints: [],
        sites: [],
        children: [],
      },
      parentUuid
    );
    bodyUuidMap.set(String(sourceBodyUuid), String(newBodyUuid));
    if (nextWorldBodyName && typeof sceneActions.setWorldBodyDisplayName === "function") {
      sceneActions.setWorldBodyDisplayName(newBodyUuid, nextWorldBodyName);
    }

    (sourceBody.joints || []).forEach((jointUuid) => {
      const joint = bundle.joints?.[jointUuid];
      if (!joint) return;
      const clonedJoint = cloneStructured(joint);
      if (clonedJoint && typeof clonedJoint === "object") delete clonedJoint.uuid;
      const newJointUuid = sceneActions.addJoint(clonedJoint, newBodyUuid);
      if (newJointUuid) jointUuidMap.set(String(jointUuid), String(newJointUuid));
    });

    (sourceBody.sites || []).forEach((siteUuid) => {
      const site = bundle.sites?.[siteUuid];
      if (!site) return;
      const clonedSite = cloneStructured(site);
      if (clonedSite && typeof clonedSite === "object") delete clonedSite.uuid;
      if (clonedSite?.material && materialKeyMap.has(String(clonedSite.material))) {
        clonedSite.material = materialKeyMap.get(String(clonedSite.material));
      }
      const newSiteUuid = sceneActions.addSite(clonedSite, newBodyUuid);
      if (newSiteUuid) siteUuidMap.set(String(siteUuid), String(newSiteUuid));
    });

    (sourceBody.geoms || []).forEach((geom) => {
      const clonedGeom = cloneStructured(geom);
      if (clonedGeom && typeof clonedGeom === "object") delete clonedGeom.uuid;
      if (clonedGeom?.mesh) {
        const nextMeshUuid = meshKeyMap.get(String(clonedGeom.mesh));
        if (!nextMeshUuid) {
          logger.warn(
            "[ObjectLibrary] Skipping saved library mesh geom without bundled mesh metadata.",
            {
              mesh: clonedGeom.mesh,
              geom: clonedGeom.name || geom?.uuid || null,
              body: sourceBody.name || sourceBodyUuid,
            }
          );
          return;
        }
        clonedGeom.mesh = nextMeshUuid;
      }
      if (Array.isArray(clonedGeom?.materials) && materialKeyMap.size > 0) {
        clonedGeom.materials = clonedGeom.materials.map(
          (materialId) => materialKeyMap.get(String(materialId)) || materialId
        );
      }
      const newGeomUuid = sceneActions.addGeom(clonedGeom, newBodyUuid);
      if (geom?.uuid && newGeomUuid) geomUuidMap.set(String(geom.uuid), String(newGeomUuid));
    });

    (sourceBody.children || []).forEach((childUuid) => {
      if (sourceBodies[childUuid]) insertBody(childUuid, newBodyUuid);
    });

    return newBodyUuid;
  };

  const historyActions = getHistoryActions();
  historyActions.beginGroup(getSceneState());
  try {
    const insertedRootUuid = insertBody(bundle.rootBodyUuid, targetParentUuid);
    if (!insertedRootUuid) return null;

    Object.entries(bundle.tendons || {}).forEach(([sourceTendonUuid, tendon]) => {
      const remapped = remapTendon(tendon, jointUuidMap, siteUuidMap, geomUuidMap);
      if (!remapped) return;
      const newTendonUuid = sceneActions.addTendon(remapped);
      if (newTendonUuid) tendonUuidMap.set(String(sourceTendonUuid), String(newTendonUuid));
    });

    Object.values(bundle.actuators || {}).forEach((actuator) => {
      const clonedActuator = cloneStructured(actuator);
      if (!clonedActuator || typeof clonedActuator !== "object") return;
      delete clonedActuator.uuid;
      const newJointUuid = clonedActuator.joint
        ? jointUuidMap.get(String(clonedActuator.joint))
        : null;
      const newTendonUuid = clonedActuator.tendonUuid
        ? tendonUuidMap.get(String(clonedActuator.tendonUuid))
        : null;
      if (!newJointUuid && !newTendonUuid) return;
      if (newJointUuid) clonedActuator.joint = newJointUuid;
      if (newTendonUuid) {
        clonedActuator.tendonUuid = newTendonUuid;
        const newTendonName = getSceneState()?.tendons?.[newTendonUuid]?.name;
        if (newTendonName) clonedActuator.tendon = newTendonName;
      }
      sceneActions.addActuator(clonedActuator);
    });

    useSelectionStore.getState().selectBody(insertedRootUuid);
    return insertedRootUuid;
  } finally {
    historyActions.endGroup(getSceneState());
  }
}
